import React from "react";

const dim = { fontFamily: "JetBrains Mono, monospace", fontSize: 11, fill: "#0369A1" };
const lbl = { fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, fontSize: 13, fill: "#0F172A" };
const cap = { fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 600, fontSize: 10.5, fill: "#0F172A" };
const mono = { fontFamily: "JetBrains Mono, monospace", fontSize: 9.5, fill: "#64748B" };
const slotTxt = { fontFamily: "JetBrains Mono, monospace", fontWeight: 700, fontSize: 8.5, fill: "#0369A1" };
const GRP_FILL = { measure: "#ECFEFF", fluidics: "#F0F9FF", electronics: "#FFFBEB", ux: "#F5F3FF", enclosure: "#F8FAFC" };
const GRP_STROKE = { measure: "#0891B2", fluidics: "#0EA5E9", electronics: "#F59E0B", ux: "#7C3AED", enclosure: "#94A3B8" };
const LANES = [{ k: "A", label: "6 IN 1", pad: "#F59E0B", lit: true }, { k: "B", label: "PHOSPHATE", pad: "#60A5FA", lit: false }, { k: "C", label: "MAGNESIUM", pad: "#DB2777", lit: false }];
const Led = ({ x, y, on }) => <circle cx={x} cy={y} r="3.6" fill={on ? "#22D3EE" : "#CBD5E1"} stroke={on ? "#0891B2" : "#94A3B8"} strokeWidth="1" />;

function Badge({ x, y, n, r = 11 }) {
  return (<g><circle cx={x} cy={y} r={r} fill="#0F172A" /><text x={x} y={y + r * 0.32} textAnchor="middle" fill="#7DD3FC" fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize={r - 1}>{n}</text></g>);
}
function C({ x, y, w, h, n, t, grp, small }) {
  return (<g><rect x={x} y={y} width={w} height={h} rx="6" fill={GRP_FILL[grp]} stroke={GRP_STROKE[grp]} strokeWidth="1.6" /><text x={x + w / 2} y={y + h / 2 + 3} textAnchor="middle" style={{ ...(small ? mono : cap), fill: "#0F172A" }}>{t}</text><Badge x={x + 12} y={y + 12} n={n} r={10} /></g>);
}
function DimH({ x1, x2, y, label }) {
  return (<g><line x1={x1} y1={y} x2={x2} y2={y} stroke="#0369A1" strokeWidth="1" /><line x1={x1} y1={y - 4} x2={x1} y2={y + 4} stroke="#0369A1" strokeWidth="1" /><line x1={x2} y1={y - 4} x2={x2} y2={y + 4} stroke="#0369A1" strokeWidth="1" /><text x={(x1 + x2) / 2} y={y - 6} textAnchor="middle" style={dim}>{label}</text></g>);
}
function DimV({ y1, y2, x, label }) {
  return (<g><line x1={x} y1={y1} x2={x} y2={y2} stroke="#0369A1" strokeWidth="1" /><line x1={x - 4} y1={y1} x2={x + 4} y2={y1} stroke="#0369A1" strokeWidth="1" /><line x1={x - 4} y1={y2} x2={x + 4} y2={y2} stroke="#0369A1" strokeWidth="1" /><text x={x - 8} y={(y1 + y2) / 2} textAnchor="middle" style={dim} transform={`rotate(-90 ${x - 8} ${(y1 + y2) / 2})`}>{label}</text></g>);
}
function ScaleBar({ x, y, px, mm }) {
  return (<g><rect x={x} y={y} width={px} height={6} fill="#0F172A" /><rect x={x} y={y} width={px / 2} height={6} fill="#38BDF8" /><text x={x} y={y - 4} style={mono}>0</text><text x={x + px} y={y - 4} textAnchor="end" style={mono}>{mm} mm</text></g>);
}

/* ============ PORTRAIT ============ */
export function InteriorPortrait() {
  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 720 640" className="w-full min-w-[620px] h-auto" role="img" aria-label="Portrait front internal elevation, dimensioned">
        <text x="330" y="30" textAnchor="middle" style={lbl}>PORTRAIT · FRONT INTERNAL ELEVATION (cover + shell removed)</text>
        <rect x="150" y="70" width="390" height="450" rx="12" fill="#FFFFFF" stroke="#334155" strokeWidth="2.5" />
        <rect x="152" y="72" width="206" height="446" fill="#F0F9FF" opacity="0.6" />
        <rect x="358" y="72" width="180" height="446" fill="#FFFBEB" opacity="0.5" />
        <text x="255" y="90" textAnchor="middle" style={{ ...mono, fill: "#0369A1" }}>WET ZONE</text>
        <text x="448" y="90" textAnchor="middle" style={{ ...mono, fill: "#B45309" }}>DRY ZONE</text>

        {/* basin + flood gutter */}
        <path d="M166,108 Q220,92 274,108" fill="none" stroke="#0EA5E9" strokeWidth="1.3" strokeDasharray="4 3" />
        <Badge x={168} y={96} n="22" r={9} />
        <path d="M168,110 Q220,98 272,110 L266,126 Q220,116 174,126 Z" fill={GRP_FILL.measure} stroke={GRP_STROKE.measure} strokeWidth="1.6" />
        <Badge x={180} y={112} n="5" r={10} />
        <text x="220" y="142" textAnchor="middle" style={mono}>catch basin</text>

        {/* 3 slots + prompt LEDs + strips */}
        {LANES.map((L, i) => {
          const x = 290 + i * 18;
          return (<g key={L.k}>
            <Led x={x} y={92} on={L.lit} />
            <rect x={x - 2.5} y="100" width="5" height="14" fill="#0F172A" />
            <rect x={x - 2.5} y="114" width="5" height="198" fill="#334155" />
            <rect x={x - 2.5} y="280" width="5" height="32" fill={L.pad} />
            <circle cx={x} cy="118" r="2.5" fill="#22D3EE" />
          </g>);
        })}
        <text x="308" y="150" textAnchor="middle" style={slotTxt}>slots A·B·C + LEDs</text>

        {/* tank */}
        <rect x="180" y="170" width="150" height="150" rx="6" fill="#FFFFFF" stroke={GRP_STROKE.measure} strokeWidth="2" />
        <rect x="188" y="214" width="134" height="98" rx="4" fill="#CFF0FF" />
        <line x1="188" y1="214" x2="322" y2="214" stroke="#7DD3FC" strokeWidth="1.4" strokeDasharray="4 3" />
        <text x="238" y="188" textAnchor="middle" style={{ ...cap, fill: "#0369A1" }}>sample tank</text>
        <Badge x={192} y={182} n="6" r={9} />
        {[0, 1, 2, 3].map((i) => (<g key={i}><line x1={200 + i * 22} y1="170" x2={200 + i * 22} y2="300" stroke={GRP_STROKE.measure} strokeWidth="4" strokeLinecap="round" /><circle cx={200 + i * 22} cy="305" r="4" fill={GRP_STROKE.measure} /></g>))}
        <Badge x={204} y={340} n="7" r={9} />
        <text x="222" y="344" style={mono}>probe block · FC · pH · Salt · Temp</text>
        {/* readers + sensor badges */}
        <Badge x={300} y={340} n="10" r={9} />
        <text x="318" y="344" style={mono}>present sensors ×3</text>
        <Badge x={300} y={250} n="21" r={8} />

        {/* fluidics */}
        <C x={170} y={378} w={90} h={42} n="11" t="flood pump" grp="fluidics" small />
        <C x={170} y={428} w={88} h={40} n="12" t="rinse 1.5L" grp="fluidics" small />
        <C x={266} y={428} w={80} h={40} n="13" t="waste 1.5L" grp="fluidics" small />

        {/* LED strip + prompt LED board on divider */}
        <rect x="356" y="150" width="6" height="220" rx="3" fill="#38BDF8" /><Badge x={359} y={140} n="4" r={9} />

        {/* dry stack */}
        <C x={372} y={110} w={156} h={52} n="3" t="touchscreen" grp="ux" />
        <C x={372} y={168} w={156} h={40} n="23" t="strip-prompt LEDs ×3" grp="ux" small />
        <C x={372} y={214} w={156} h={46} n="14" t="MCU / control PCB" grp="electronics" small />
        <C x={372} y={266} w={156} h={42} n="15" t="analog + optical AFE" grp="electronics" small />
        <C x={372} y={314} w={72} h={42} n="16" t="comms" grp="electronics" small />
        <C x={456} y={314} w={72} h={42} n="18" t="charger" grp="electronics" small />
        <C x={372} y={362} w={156} h={48} n="17" t="Li-ion battery" grp="electronics" />
        <text x="450" y="430" textAnchor="middle" style={mono}>(19) service cover behind</text>

        {[176, 300, 400, 512].map((fx) => <rect key={fx} x={fx} y="516" width="26" height="12" rx="4" fill={GRP_FILL.enclosure} stroke={GRP_STROKE.enclosure} strokeWidth="1.4" />)}
        <Badge x={176} y={534} n="20" r={9} />

        <DimH x1={150} x2={540} y={58} label="260 mm" />
        <DimV y1={70} y2={520} x={136} label="300 mm" />
        <ScaleBar x={150} y={560} px={77} mm={50} />
        <text x="150" y="600" style={mono}>● lit prompt LED = insert that strip. Wet zone isolated from dry electronics.</text>
      </svg>
    </div>
  );
}

/* ============ LANDSCAPE ============ */
export function InteriorLandscape() {
  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 760 600" className="w-full min-w-[640px] h-auto" role="img" aria-label="Landscape side internal section, dimensioned">
        <text x="360" y="30" textAnchor="middle" style={lbl}>LANDSCAPE · SIDE INTERNAL SECTION (cover removed)</text>
        <text x="150" y="52" style={{ ...mono, fill: "#0369A1" }}>◄ FRONT</text>
        <text x="590" y="52" textAnchor="end" style={{ ...mono, fill: "#B45309" }}>REAR ►</text>
        <rect x="150" y="70" width="450" height="450" rx="12" fill="#FFFFFF" stroke="#334155" strokeWidth="2.5" />
        <rect x="152" y="72" width="230" height="446" fill="#F0F9FF" opacity="0.55" />
        <rect x="382" y="72" width="216" height="446" fill="#FFFBEB" opacity="0.5" />

        <C x={156} y={110} w={40} h={210} n="3" t="" grp="ux" />
        <text x="176" y="220" textAnchor="middle" style={cap} transform="rotate(-90 176 220)">touchscreen</text>
        <rect x="198" y="120" width="6" height="190" rx="3" fill="#38BDF8" /><Badge x={201} y={110} n="4" r={9} />

        {/* basin + gutter */}
        <path d="M244,110 Q360,94 476,110" fill="none" stroke="#0EA5E9" strokeWidth="1.3" strokeDasharray="4 3" /><Badge x={246} y={100} n="22" r={9} />
        <path d="M250,112 Q360,98 470,112 L462,128 Q360,116 258,128 Z" fill={GRP_FILL.measure} stroke={GRP_STROKE.measure} strokeWidth="1.6" />
        <Badge x={262} y={114} n="5" r={9} />
        {/* 3 slots + LEDs at front of basin */}
        {LANES.map((L, i) => { const x = 236 + i * 14; return (<g key={L.k}><Led x={x + 2} y={94} on={L.lit} /><rect x={x} y="102" width="5" height="16" fill="#0F172A" /></g>); })}
        <text x="226" y="98" textAnchor="end" style={{ ...mono, fill: "#0369A1" }}>slots + LEDs</text>

        <rect x="230" y="160" width="150" height="170" rx="6" fill="#FFFFFF" stroke={GRP_STROKE.measure} strokeWidth="2" />
        <rect x="238" y="214" width="120" height="108" rx="4" fill="#CFF0FF" />
        <rect x="360" y="204" width="10" height="118" fill="#94A3B8" />
        <text x="255" y="180" textAnchor="middle" style={{ ...cap, fill: "#0369A1" }}>sample tank</text>
        <Badge x={242} y={172} n="6" r={9} />
        {[0, 1, 2, 3].map((i) => <line key={i} x1={252 + i * 20} y1="160" x2={252 + i * 20} y2="316" stroke={GRP_STROKE.measure} strokeWidth="3.5" strokeLinecap="round" />)}
        <Badge x={256} y={346} n="7" r={9} /><text x="272" y="350" style={mono}>probes submerged</text>
        {LANES.map((L, i) => { const x = 338 + i * 8; return <rect key={L.k} x={x} y="118" width="4" height="204" fill="#334155" />; })}
        <Badge x={344} y={402} n="8·9·21" r={9} />

        <C x={228} y={430} w={90} h={44} n="11" t="flood pump" grp="fluidics" small />
        <C x={326} y={430} w={54} h={44} n="12" t="rinse" grp="fluidics" small />

        <C x={392} y={110} w={150} h={44} n="14" t="MCU PCB" grp="electronics" small />
        <C x={392} y={160} w={150} h={44} n="15" t="analog + optical AFE" grp="electronics" small />
        <C x={392} y={210} w={92} h={40} n="16" t="comms" grp="electronics" small />
        <C x={392} y={256} w={150} h={50} n="17" t="Li-ion battery" grp="electronics" />
        <C x={392} y={314} w={92} h={42} n="18" t="charger" grp="electronics" small />
        <rect x="560" y="90" width="30" height="410" rx="6" fill={GRP_FILL.enclosure} stroke={GRP_STROKE.enclosure} strokeWidth="1.6" />
        <text x="575" y="300" textAnchor="middle" style={cap} transform="rotate(-90 575 300)">service cover</text>
        <Badge x={575} y={104} n="19" r={9} />
        {["DC", "USB-C", "tap", "drain"].map((p, i) => <g key={p}><rect x="600" y={140 + i * 70} width="26" height="26" rx="5" fill="#E0F2FE" stroke="#0891B2" strokeWidth="1.4" /><text x="632" y={157 + i * 70} style={mono}>{p}</text></g>)}

        {[176, 320, 470, 540].map((fx) => <rect key={fx} x={fx} y="516" width="24" height="12" rx="4" fill={GRP_FILL.enclosure} stroke={GRP_STROKE.enclosure} strokeWidth="1.4" />)}
        <Badge x={176} y={534} n="20" r={9} />

        <DimH x1={150} x2={600} y={58} label="240 mm deep" />
        <DimV y1={70} y2={520} x={136} label="300 mm" />
        <ScaleBar x={150} y={560} px={75} mm={50} />
      </svg>
    </div>
  );
}

/* ============ AERIAL ============ */
export function InteriorAerial() {
  return (
    <div className="overflow-x-auto">
      <svg viewBox="0 0 720 620" className="w-full min-w-[620px] h-auto" role="img" aria-label="Aerial top-down internal plan, dimensioned">
        <text x="360" y="30" textAnchor="middle" style={lbl}>AERIAL · TOP-DOWN INTERNAL PLAN</text>
        <text x="150" y="512" style={{ ...mono, fill: "#0369A1" }}>FRONT edge ▲</text>
        <text x="560" y="72" textAnchor="end" style={{ ...mono, fill: "#B45309" }}>REAR edge ▼</text>
        <rect x="150" y="80" width="400" height="370" rx="12" fill="#FFFFFF" stroke="#334155" strokeWidth="2.5" />
        <rect x="152" y="82" width="210" height="366" fill="#F0F9FF" opacity="0.55" />
        <rect x="362" y="82" width="186" height="366" fill="#FFFBEB" opacity="0.5" />
        <text x="255" y="100" textAnchor="middle" style={{ ...mono, fill: "#0369A1" }}>WET ZONE</text>
        <text x="456" y="100" textAnchor="middle" style={{ ...mono, fill: "#B45309" }}>DRY ZONE</text>

        {/* basin + flood gutter ring */}
        <ellipse cx="250" cy="150" rx="100" ry="60" fill="none" stroke="#0EA5E9" strokeWidth="1.3" strokeDasharray="5 4" /><Badge x={168} y={112} n="22" r={9} />
        <ellipse cx="250" cy="150" rx="90" ry="52" fill={GRP_FILL.measure} stroke={GRP_STROKE.measure} strokeWidth="1.6" />
        <circle cx="250" cy="150" r="11" fill="#BAE6FD" stroke="#0369A1" strokeWidth="1.6" /><circle cx="250" cy="150" r="4" fill="#0369A1" />
        <Badge x={186} y={128} n="5" r={9} />
        <text x="250" y="118" textAnchor="middle" style={{ ...mono, fill: "#0369A1" }}>catch basin (rear)</text>

        <rect x="196" y="214" width="118" height="90" rx="6" fill="#CFF0FF" stroke={GRP_STROKE.measure} strokeWidth="1.8" />
        <Badge x={208} y={226} n="6" r={9} /><text x="255" y="264" textAnchor="middle" style={{ ...mono, fill: "#0369A1" }}>sample tank</text>
        <rect x="196" y="314" width="80" height="24" rx="5" fill={GRP_FILL.measure} stroke={GRP_STROKE.measure} strokeWidth="1.6" /><Badge x={208} y={326} n="7" r={9} /><text x="290" y="330" style={mono}>probe block</text>

        {/* 3 slots front + LEDs + labels */}
        {LANES.map((L, i) => { const x = 208 + i * 40; return (<g key={L.k}>
          <rect x={x} y="360" width="6" height="22" fill="#0F172A" />
          <Led x={x + 3} y={392} on={L.lit} />
          <text x={x + 3} y={410} textAnchor="middle" style={{ ...slotTxt, fontSize: 7.5 }}>{L.label}</text>
        </g>); })}
        <Badge x={196} y={368} n="8·9·21" r={8} /><Badge x={330} y={368} n="10" r={8} />

        <C x={376} y={120} w={158} h={50} n="14" t="MCU / control PCB" grp="electronics" />
        <C x={376} y={178} w={158} h={44} n="15" t="analog + optical AFE" grp="electronics" small />
        <C x={376} y={230} w={74} h={40} n="16" t="comms" grp="electronics" small />
        <C x={460} y={230} w={74} h={40} n="18" t="charger" grp="electronics" small />
        <C x={376} y={278} w={158} h={52} n="17" t="Li-ion battery" grp="electronics" />
        <C x={376} y={340} w={100} h={42} n="11" t="flood pump" grp="fluidics" small />
        <C x={482} y={340} w={52} h={42} n="12/13" t="r/w" grp="fluidics" small />

        <DimH x1={150} x2={550} y={68} label="260 mm" />
        <DimV y1={80} y2={450} x={136} label="240 mm deep" />
        <ScaleBar x={150} y={476} px={77} mm={50} />
        <text x="230" y="516" style={mono}>Basin at rear; three labelled 6 mm slots + prompt LEDs on the accessible front edge.</text>
      </svg>
    </div>
  );
}

/* ============ EXPLODED ASSEMBLY ============ */
export function ExplodedAssembly() {
  const plates = [
    { n: "5·22", t: "Catch basin + flood-rinse gutter", grp: "measure" },
    { n: "6", t: "Overflow-weir tank", grp: "measure" },
    { n: "7", t: "Probe block", grp: "measure" },
    { n: "8·9·21·10", t: "3 strip slots + readers + sensors", grp: "measure" },
    { n: "11·12·13", t: "Fluidics deck (flood pump · rinse · waste)", grp: "fluidics" },
    { n: "3·4·23", t: "Touchscreen · status LED · prompt LEDs", grp: "ux" },
    { n: "14·15·16", t: "PCB stack (MCU · AFE · comms)", grp: "electronics" },
    { n: "17·18", t: "Battery + charger", grp: "electronics" },
    { n: "2", t: "Chassis / wet-dry divider", grp: "enclosure" },
    { n: "1·19", t: "Outer shell + service cover", grp: "enclosure" },
    { n: "20", t: "Base + feet", grp: "enclosure" },
  ];
  const sk = 46, plateW = 260, plateH = 26, stepY = 84, x0 = 250, y0 = 60;
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 790 ${y0 + plates.length * stepY + 60}`} className="w-full min-w-[620px] h-auto" role="img" aria-label="Exploded assembly, numbered">
        <text x="390" y="34" textAnchor="middle" style={lbl}>3D EXPLODED ASSEMBLY · top → base</text>
        <line x1={x0 + plateW / 2 + sk / 2} y1={y0 - 10} x2={x0 + plateW / 2 + sk / 2} y2={y0 + plates.length * stepY} stroke="#CBD5E1" strokeWidth="2" strokeDasharray="5 6" />
        {plates.map((p, i) => {
          const y = y0 + i * stepY, fill = GRP_FILL[p.grp], stroke = GRP_STROKE[p.grp];
          return (<g key={p.n}>
            <polygon points={`${x0},${y} ${x0 + plateW},${y} ${x0 + plateW + sk},${y - sk / 2} ${x0 + sk},${y - sk / 2}`} fill={fill} stroke={stroke} strokeWidth="1.6" />
            <polygon points={`${x0},${y} ${x0 + plateW},${y} ${x0 + plateW},${y + plateH} ${x0},${y + plateH}`} fill={fill} stroke={stroke} strokeWidth="1.6" opacity="0.85" />
            <polygon points={`${x0 + plateW},${y} ${x0 + plateW + sk},${y - sk / 2} ${x0 + plateW + sk},${y - sk / 2 + plateH} ${x0 + plateW},${y + plateH}`} fill={stroke} opacity="0.18" stroke={stroke} strokeWidth="1.2" />
            <Badge x={x0 + 18} y={y + plateH / 2} n={p.n} r={11} />
            <text x={x0 + 44} y={y + plateH / 2 + 4} style={cap}>{p.t}</text>
            <line x1={x0 + plateW / 2 + sk / 2} y1={y - sk / 4} x2={x0 + plateW + sk + 10} y2={y - sk / 4} stroke="#7DD3FC" strokeWidth="1.2" />
          </g>);
        })}
        <text x={x0} y={y0 + plates.length * stepY + 34} style={mono}>Parts pulled along the vertical build axis. Numbers key to the component catalog.</text>
      </svg>
    </div>
  );
}

/* ============ PART GLYPH ============ */
export function PartGlyph({ shape, grp }) {
  const f = GRP_FILL[grp], s = GRP_STROKE[grp];
  const box = (children) => (<svg viewBox="0 0 80 64" className="w-full h-16" role="img" aria-hidden>{children}</svg>);
  switch (shape) {
    case "board": return box(<><rect x="10" y="14" width="60" height="36" rx="3" fill={f} stroke={s} strokeWidth="2" />{[20, 30, 40, 50, 60].map((cx) => <circle key={cx} cx={cx} cy="24" r="2" fill={s} />)}<rect x="18" y="34" width="18" height="10" rx="2" fill={s} opacity="0.4" /></>);
    case "box": return box(<><polygon points="14,22 54,22 66,14 26,14" fill={f} stroke={s} strokeWidth="2" /><rect x="14" y="22" width="40" height="30" fill={f} stroke={s} strokeWidth="2" /><polygon points="54,22 66,14 66,44 54,52" fill={s} opacity="0.2" stroke={s} strokeWidth="1.5" /></>);
    case "cylinder": return box(<><ellipse cx="40" cy="18" rx="18" ry="6" fill={f} stroke={s} strokeWidth="2" /><path d="M22,18 L22,46 A18,6 0 0 0 58,46 L58,18" fill={f} stroke={s} strokeWidth="2" /></>);
    case "bottle": return box(<><rect x="34" y="10" width="12" height="10" fill={f} stroke={s} strokeWidth="2" /><rect x="26" y="20" width="28" height="34" rx="5" fill={f} stroke={s} strokeWidth="2" /><line x1="26" y1="40" x2="54" y2="40" stroke={s} strokeWidth="1.5" strokeDasharray="3 2" /></>);
    case "probe": return box(<><rect x="24" y="10" width="32" height="14" rx="3" fill={f} stroke={s} strokeWidth="2" />{[30, 38, 46].map((cx) => <line key={cx} x1={cx} y1="24" x2={cx} y2="52" stroke={s} strokeWidth="3" strokeLinecap="round" />)}</>);
    case "sensor": return box(<><rect x="24" y="14" width="20" height="36" rx="3" fill={f} stroke={s} strokeWidth="2" /><circle cx="52" cy="32" r="7" fill="none" stroke={s} strokeWidth="2" /><line x1="44" y1="32" x2="46" y2="32" stroke={s} strokeWidth="2" /></>);
    case "bowl": return box(<><path d="M18,20 Q40,14 62,20 L54,44 Q40,50 26,44 Z" fill={f} stroke={s} strokeWidth="2" /><circle cx="40" cy="34" r="4" fill="#BAE6FD" stroke={s} strokeWidth="1.5" /></>);
    case "screen": return box(<><rect x="16" y="12" width="48" height="34" rx="3" fill="#0F172A" stroke={s} strokeWidth="2" /><rect x="22" y="18" width="36" height="22" rx="2" fill="#38BDF8" opacity="0.5" /></>);
    case "panel": return box(<><rect x="14" y="12" width="52" height="40" rx="4" fill={f} stroke={s} strokeWidth="2" /><rect x="20" y="18" width="20" height="12" rx="2" fill={s} opacity="0.25" /></>);
    case "foot": return box(<>{[26, 54].map((cx) => <ellipse key={cx} cx={cx} cy="40" rx="10" ry="6" fill={f} stroke={s} strokeWidth="2" />)}</>);
    default: return box(<rect x="16" y="16" width="48" height="32" rx="4" fill={f} stroke={s} strokeWidth="2" />);
  }
}
